import firebaseConfig from "./firebase-config";
import * as firebase from "firebase/app";
import "firebase/messaging";
import messages from "./push-messages";
import Toast from "../../../components/modal/toast";

const storageItemName = "sentFirebaseMessagingToken";

/**
 * Пуш-уведомления
 * @class Push
 * @description Управляет отпиской/подпиской на пуш-уведомления и их отправкой
 * 
 * @property {HTMLElement[]} Переключатели подписки
 * @property {Function} messaging
 * @property {Boolean} inProcess
 * @property {String} token
 * @property {Boolean} checked
 */
class Push {
  constructor() {
    this.switches = [];
    this.messaging;
    this._inProcess = false;
    this.checked = false;
  }

  get inProcess() {
    return this._inProcess;
  }

  set inProcess(value) {
    this._inProcess = Boolean(value);
    this.switches.forEach(el => el.disabled = this._inProcess);
  }

  get token() {
    return this._token;
  }

  set token(value) {
    this._token = value;
    if (!this._token) {
      this._setTokenSentToServer(false);
    }
  }

  get checked() {
    return this._checked;
  }

  set checked(value) {
    this._checked = Boolean(value);
    this._toggleSwitches(this._checked);
  }

  /**
   * Инициализирует переключатели и firebase
   */
  init() {
    if (!this._checkNavigator()) return;

    try {
      firebase.initializeApp(firebaseConfig);
      this.messaging = firebase.messaging();

      this.messaging.onMessage(payload => this._onMessage(payload));
      this.messaging.onTokenRefresh(() => this._onTokenRefresh());

      this.switches = Array.from(document.querySelectorAll('input[name="push-notifications-toggle"]'));
      
      this.switches.forEach(el => {
        el.disabled = false;
        el.addEventListener('change', () => this._onSwitchChange(el.checked));
      });
      this.checked = !!this._getSavedToken();

      document.querySelectorAll("[data-subscribe-push]")
      .forEach(button => {
        button.addEventListener("click", e => {
          this._onSwitchChange(true);
        });
      });

      this.inited = true;
    } catch(error) {
      console.error(error);
    }
    
  }

  /**
   * Проверяет, доступны ли пуш-сообщения в браузере
   * @returns {Boolean}
   */
  _checkNavigator() {
    if (
      'Notification' in window &&
      'serviceWorker' in navigator &&
      'localStorage' in window &&
      'fetch' in window &&
      'postMessage' in window
    ) return true;
    return false;
  }

  /**
   * Подписывает пользователя на уведомления
   */
  subscribe() {
    if (this.inProcess) return;

    this.inProcess = true;

    // проверить разрешение
    this._requestPermission()
      .then(() => {
        // получить токен
        return this._getToken()
      })
      .then(token => {
        this.token = token;
        // отправить на сервер
        return this.save(true, token)
          .then(() => {
            this._setTokenSentToServer();
            this.checked = true;
            this.inProcess = false;
          })
          .catch(err => {
            this.token = false;
            this.checked = false;
            this.inProcess = false;
            console.error(err);
          })
      })
      .catch((err) => {
        console.error(err);
        this.inProcess = false;
        this.checked = false;
      });
  }

  /**
   * Отписывает пользователя от уведомлений
   */
  unsubscribe() {
    this.inProcess = true;

    let oldToken = this._getSavedToken();

    this._getToken()
      .then(token => {
        this.token = token;
        return this._deleteToken();
      })
      .then(() => {
        this.checked = false;
        this.token = false;
        this.inProcess = false;
        if (oldToken) {
          this.save(false, oldToken);
        }
      })
      .catch(err => {
        this.inProcess = false;
        this.checked = true;
        console.error(err);
      });
  }

  /**
   * Сохранение состояния подписки 
   * @param {Boolean} checked 
   * @returns {Promise}
   */
  save(checked, token) {
    
    let postData = new FormData();
    postData.append("push", checked);
    postData.append("token", token);

    let error = checked ? 'fail-subscribe' : 'fail-unsubscribe';
    let success = checked ? 'subscribed' : 'unsubscribed';

    // let method = checked ? 'store_pushes' : 'delete_pushes';
    let method = checked ? '/u/push/subscribe' : '/u/push/unsubscribe';

    return new Promise((resolve, reject) => {
      // API(method, null, postData)
      fetch(method, {
        method: "POST",
        body: postData
      })
      .then(res => res.json())
      .then(res => {
        if (!res.success) {
          this.showMessage(error);
          reject(error);
        } else {
          this.showMessage(success);
          resolve();
        }
      })
      .catch(err => {
        console.error(err);
        this.showMessage(error);
        reject(error);
      });
    });
    // return new Promise((resolve, reject) => {
    //   API(method, null, postData)
    //     .then(res => {
    //       if (!res.success) {
    //         this.showMessage(error);
    //         reject(error);
    //       } else {
    //         this.showMessage(success);
    //         resolve();
    //       }
    //     })
    //     .catch(err => {
    //       console.error(err);
    //       this.showMessage(error);
    //       reject(error);
    //     });
    // });
  }

  /**
   * Запрос разрешения на уведомления
   * @returns {Promise}
   */
  _requestPermission() {
    return new Promise((resolve, reject) => {
      if (!("Notification" in window)) {
        // нет уведомлений в браузере
        this.showMessage('no-notifications');
        reject("Нет уведомлений в браузере");
      } else if (Notification.permission === "denied") {
        // в разрешении уже отказано
        this.showMessage('already-denied');
        reject("Отказано в разрешении");
      } else {
        this.messaging.requestPermission()
          .then(() => resolve())
          .catch(() => {
            this.showMessage('deny-now');
            return reject("Отказано в разрешении");
          })
      }
    })

  }

  /**
   * Получает токен устройства
   * @returns {Promise}
   */
  _getToken() {
    let error = this.checked ? 'fail-unsubscribe' : 'fail-subscribe';
    return new Promise((resolve, reject) => {
      this.messaging.getToken()
        .then(token => {
          if (token) resolve(token);
          else {
            this.token = false;
            this.showMessage(error);
            reject('Не удалось получить токен');
          }
        })
        .catch(err => {
          console.error(err);
          this.token = false;
          this.showMessage(error);
          reject('Не удалось получить токен');
        });
    });
  }

  /**
   * Удаляет токен из firebase
   * @returns {Promise}
   */
  _deleteToken() {
    return new Promise((resolve, reject) => {
      this.messaging.deleteToken(this.token)
        .then(() => {
          resolve();
        })
        .catch((err) => {
          console.error(err);
          reject("Не удалось удалить токен");
        })
    })
  }

  /**
   * Отмечает, что токен уже отправлялся на сервер
   */ 
  _setTokenSentToServer() {
    if (this.token) {
      window.localStorage.setItem(storageItemName, this.token);
    } else {
      window.localStorage.removeItem(storageItemName);
    }
  }

  /**
   * Проверяет, отправлялся ли токен на сервер
   */
  _isTokenSentToServer() {
    return window.localStorage.getItem(storageItemName) === this.token;
  }

  /**
   * Возвращает токен из хранилища
   */
  _getSavedToken() {
    return window.localStorage.getItem(storageItemName);
  }

  /**
   * Обрабатывает полученные сообщения
   * @param {*} payload 
   */
  _onMessage(payload) {
    // register fake ServiceWorker for show notification on mobile devices
    // navigator.serviceWorker.register('/serviceworker/firebase-messaging-sw.js');
    navigator.serviceWorker.register(window.firebaseSWUrl || '/serviceworker/firebase-messaging-sw.js');
    Notification.requestPermission(function(permission) {
        if (permission === 'granted') {
            navigator.serviceWorker.ready.then(function(registration) {
              // Copy data object to get parameters in the click handler
              payload.data.data = JSON.parse(JSON.stringify(payload.data));

              registration.showNotification(payload.data.title, payload.data);
            }).catch(function(error) {
                // registration failed :(
                showError('ServiceWorker registration failed', error);
            });
        }
    });
  }

  _onTokenRefresh() {
    this.getToken()
      .then(refreshedToken => {
        this.token = refreshedToken;

        return this.save(true, refreshedToken)
          .then(() => {
            this._setTokenSentToServer();
            this.checked = true;
          })
          .catch(err => {
            this.token = false;
            this.checked = false;
            console.error(err);
          })
      })
      .catch(err => {
        console.error(err);
      })
  }


  /**
   * Переключает все тумблеры подписки
   * @param {Boolean} checked 
   */
  _toggleSwitches(checked) {
    this.switches.forEach(function(el) {
      el.checked = checked;
    });
  }

  /**
   * Обработка переключения тумблера
   * @param {Boolean} checked 
   */
  _onSwitchChange(checked) {
    if (checked) this.subscribe();
    else this.unsubscribe();
  }


  /**
   * Вывод сообщения
   * @param {string} messageCode 
   */
  showMessage(messageCode) {
    let message = messages[messageCode];

    if (message && message.text) {
      Toast.open(message.text, { state: message.status });
    }
  }
}

let push = new Push();

window.onDomReady.then(() => {
  const pushToast = document.getElementById("push-toast");

  push.init();

  if (pushToast && !push.checked) {
    const [ rule, value ] = pushToast.dataset.showRule.split("=");

    setTimeout(() => pushToast.style.display = "block", 0);

    const closePushToast = () => {
      pushToast.classList.add("disappears");
      pushToast.ontransitionend = () => {
        pushToast.ontransitionend = null;
        pushToast.parentNode.removeChild(pushToast);
      };

      const maxAge = 60 * 60 * 24 * 30;
      document.cookie = "seenPushToast=seenPushToast; path=/; max-age=" + maxAge;
    };

    const initPushToast = () => {
        pushToast.classList.add("visible");

        pushToast.addEventListener("click", e => {
          if (e.target.closest("[data-subscribe-push]"))
          {
            closePushToast();
          }

          if (e.target.closest("[data-close-toast]"))
          {
            closePushToast();
          }
        });

        setTimeout(closePushToast, 5000);
    };

    const docScroll = () => {
      const h = document.documentElement, 
          b = document.body,
          st = 'scrollTop',
          sh = 'scrollHeight';
      return (h[st]||b[st]) / ((h[sh]||b[sh]) - h.clientHeight) * 100;
    };

    if (rule === "timeout")
    {
      setTimeout(initPushToast, +value);
    }
    else if (rule === "scroll")
    {
      const percent = Math.max(0, Math.min(100, +value));

      window.addEventListener("scroll", function scrollListener() {
        if (docScroll() >= percent)
        {
          initPushToast();
          window.removeEventListener("scroll", scrollListener);
        }
      });
    }
  }
});